// Pieza del descuento de accesorios (slot `accessoryDiscount`): al entrar el primer accesorio que
// califica, aparta la promo en la ancla sin `lineIds`; sin accesorios que califiquen, la suelta.
import { useEffect, useRef, useState } from 'react';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { useSalePromotionEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import { useAnchorSaleContext } from '../hooks/useAnchorSaleContext';
import { useApplyPromotions } from '../hooks/useApplyPromotions';
import {
    accessoryDiscountCandidate,
    accessoryOnlyIds,
    heldAccessoryDiscounts,
    withoutPromotionsOn,
} from '../lib/accessoryDiscount';
import { promotionErrorCopy } from '../lib/promotionErrorCopy';
import { rememberedPromotionName } from '../lib/promotionNames';
import { normalizeKinds, withSolo, withStackable } from '../lib/promotionSelection';
import { useSessionPromotionState } from '../store/promotionSelectionStore';

export interface AccessoryDiscountSyncProps {
    sessionId: string;
    context: AnchorSlotContext;
}

const NOT_WITH_PHONE_PROMO = 'El descuento de accesorios no se suma con la promo del teléfono.';

export function AccessoryDiscountSync({ sessionId, context }: AccessoryDiscountSyncProps) {
    const { anchorLine, quoteState } = context;
    const extraLines = context.extraLines ?? [];
    const { synced, selection } = useSessionPromotionState(sessionId);
    const evaluation = useSalePromotionEvaluation(sessionId);
    const { data: store } = useStoreSalePromotions(sessionId);
    const { withoutSureKeep } = useAnchorSaleContext(sessionId, anchorLine);
    const { apply, isApplying } = useApplyPromotions({ sessionId, context });
    const [rejection, setRejection] = useState<{ key: string; message: string }>();
    const tried = useRef(new Set<string>());

    const storeItems = store?.items ?? [];
    const data = evaluation.isPlaceholderData ? undefined : evaluation.data;
    const anchorId = anchorLine.lineId;
    const held = data
        ? heldAccessoryDiscounts(selection, anchorId, accessoryOnlyIds(data, storeItems))
        : [];
    const candidate = data
        ? accessoryDiscountCandidate(data, storeItems, extraLines, withoutSureKeep)
        : undefined;
    const view = data ? normalizeKinds(selection, data) : selection;
    const phoneSolo = view.byLine[anchorId]?.solo ?? null;
    const clashes =
        candidate !== undefined &&
        !candidate.stackable &&
        phoneSolo !== null &&
        phoneSolo.promotionId !== candidate.promotionId;
    const key = candidate ? `${anchorId}|${candidate.promotionId}` : '';
    const ready = synced && data !== undefined && !isApplying && quoteState !== 'charged';

    // Sin accesorios que califiquen, se olvida lo intentado: el siguiente vuelve a apartar.
    useEffect(() => {
        if (candidate === undefined) tried.current.clear();
    }, [candidate]);

    useEffect(() => {
        if (!ready) return;
        if (candidate === undefined && held.length > 0) {
            const releasing = `release|${anchorId}|${held.join('|')}`;
            if (tried.current.has(releasing)) return;
            tried.current.add(releasing);
            apply((base) => withoutPromotionsOn(base, anchorId, held)).catch(() => undefined);
            return;
        }
        if (!candidate || held.length > 0 || clashes || tried.current.has(key)) return;
        tried.current.add(key);
        const choice = {
            promotionId: candidate.promotionId,
            promotionVersion: candidate.version,
            giftSkus: [],
        };
        apply((base) => {
            const next = { ...base, anchorLineId: base.anchorLineId ?? anchorId };
            const anchor = next.anchorLineId;
            return candidate.stackable
                ? withStackable(next, anchor, choice, true)
                : withSolo(next, anchor, choice);
        })
            .then(() => {
                setRejection(undefined);
            })
            .catch((error: unknown) => {
                setRejection({
                    key,
                    message: promotionErrorCopy(error, (id) =>
                        rememberedPromotionName(sessionId, id),
                    ),
                });
            });
    });

    const message =
        rejection?.key === key && held.length === 0
            ? rejection.message
            : clashes && held.length === 0
              ? NOT_WITH_PHONE_PROMO
              : undefined;
    if (!message) return null;
    return (
        <p role="status" className="text-[12px] text-warning">
            {candidate ? `${candidate.name}: ${message}` : message}
        </p>
    );
}
